/*
 * Copyright(c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { ViewerContextInfo } from '@app/di/data-model/viewer-context-info';
import { OnlineContextItem } from '@app/di/data-model/online-context-item';
import { ViewerContextItem } from '@app/di/data-model/viewer-context-item';
import { UtilService } from '@app/util/util.service';
import { appConstants } from '@app/common/app-constants';

@Injectable({
  providedIn: 'root'
})
export class ContextService {
  private contextTokenMax = 1000;
  private contextTokenMin = 1;
  private token: any;
  private PATIENT_NAME_FORMAT_CPACS = 'CPACS_LOCAL';
  private PATIENT_NAME_FORMAT_DICOM = 'DICOM';
  private priorContextMap = new Map();
  private docScanStatusMap = new Map();
  private contextInfo = new ViewerContextInfo();

  constructor(private utilService: UtilService) {}

  setCurrentOnlineContextItem(onlineItem: OnlineContextItem, workItem: any) {
    const patientNameObj = workItem.patientNameObj;
    onlineItem.repositoryType = workItem.repositoryType;
    onlineItem.siteId = workItem.siteId;
    onlineItem.repositoryId = workItem.repositoryId;
    const isNameEmpty =
      _.isEmpty(patientNameObj.family) &&
      _.isEmpty(patientNameObj.given) &&
      _.isEmpty(patientNameObj.middle) &&
      _.isEmpty(patientNameObj.prefix) &&
      _.isEmpty(patientNameObj.suffix);
    if (isNameEmpty || _.isEmpty(workItem.repositoryType) || workItem.repositoryType === 'Local') {
      onlineItem.patientName = patientNameObj.text;
      onlineItem.patientNameFormat = this.PATIENT_NAME_FORMAT_CPACS;
    } else {
      onlineItem.patientName = [
        patientNameObj.family,
        patientNameObj.given,
        patientNameObj.middle,
        patientNameObj.prefix,
        patientNameObj.suffix
      ].join('^');
      onlineItem.patientNameFormat = this.PATIENT_NAME_FORMAT_DICOM;
    }
    onlineItem.modality = workItem.modality;

    onlineItem.patientIdentifier = workItem.mrn;
    onlineItem.DOB = workItem.DOB ? workItem.DOB.split('-').join('') : '';
    // set up patient gender according to repositoryType
    if (workItem.patientGender) {
      if (onlineItem.repositoryType) {
        onlineItem.patientGender = workItem.patientGender.charAt(0).toUpperCase();
      } else {
        onlineItem.patientGender = workItem.patientGender;
      }
    }
    // set up patient identifier according to repositoryType
    onlineItem = this.setCurrentOnlineContextItemIdentifier(onlineItem);
    return onlineItem;
  }

  setCurrentOnlineContextItemIdentifier(onlineItem: OnlineContextItem) {
    if (!!onlineItem.repositoryType && onlineItem.repositoryType !== 'Local') {
      // REMOTE study has only one domain and doesn't have domainOID(.system)
      const remoteIdentifier = onlineItem.patientIdentifier[0];
      onlineItem.patientIdWithIssuer =
        '|' + this.getSanitizedValue(remoteIdentifier.value) + '$' + this.getSanitizedValue(remoteIdentifier.display);
    } else {
      // LOCAL study could have multiple domains
      for (const localIdentifier of onlineItem.patientIdentifier) {
        const identifierWithIssuer =
          this.getSanitizedValue(localIdentifier.system) +
          '|' +
          this.getSanitizedValue(localIdentifier.value) +
          '$' +
          this.getSanitizedValue(localIdentifier.display);
        if (onlineItem.patientIdWithIssuer === '') {
          onlineItem.patientIdWithIssuer = identifierWithIssuer;
        } else {
          onlineItem.patientIdWithIssuer = onlineItem.patientIdWithIssuer + ',' + identifierWithIssuer;
        }
      }
    }
    return onlineItem;
  }

  getSanitizedValue(value: string) {
    return value && !!value.trim() ? value.trim() : '';
  }

  getCurrentOnlineContextItem(examIdentifier) {
    let NCD;
    if (examIdentifier !== undefined && examIdentifier != null && examIdentifier.indexOf(':') > -1) {
      NCD = UtilService.parseInt(examIdentifier.split(':')[1]);
    }
    const index = this.findIndexById(NCD, this.contextInfo.onlineContextItemList);
    const currentItem = this.contextInfo.onlineContextItemList[index];

    if (index > -1) {
      return {
        patientName: currentItem.patientName,
        patientNameFormat: currentItem.patientNameFormat,
        DOB: currentItem.DOB,
        modality: currentItem.modality,
        patientGender: currentItem.patientGender,
        patientIdWithIssuer: currentItem.patientIdWithIssuer,
        repositoryType: currentItem.repositoryType,
        repositoryId: currentItem.repositoryId,
        siteId: currentItem.siteId
      };
    }
  }

  setContext(workitem) {
    // To hide the viewer on preview another study set the index as -1.
    if (null === workitem) {
      this.contextInfo.currentIndex = -1;
      this.contextInfo.onlineContextIndex = -1;
    } else {
      const index = this.findIndexById(workitem.requestedProcedureId, this.contextInfo.contextItemList);
      const studyFound = index > -1;

      if (studyFound) {
        this.contextInfo.contextItemList[index].studyDataAvailability = workitem.studyDataAvailability;
        this.contextInfo.contextItemList[index].imageCount = workitem.imageCount;
        // If study is online and imageCount > 0 find the index from online context list
        if (
          this.contextInfo.contextItemList[index].archiveAdoptedStatus ===
            appConstants.archiveAdoptionStatus.ArchiveAdopted ||
          (UtilService.isExamOnline(this.contextInfo.contextItemList[index].studyDataAvailability) &&
            workitem.imageCount > 0)
        ) {
          // Add exam context in to online context list if it is not present and disableAutoLaunchViewer=FALSE
          // workitem.disableAutoLaunchViewer FALSE means launch viewer
          // workitem.disableAutoLaunchViewer becomes FALSE when
          // - user launches an exam from unsigned worklist or custom worklist with auto launch viewer preference ON
          // OR
          // - user launches an exam from unsigned worklist or custom worklist with auto launch viewer preference OFF
          // and then clicks on View Images button
          const studyFoundInOnlineContextlist =
            this.findIndexById(workitem.requestedProcedureId, this.contextInfo.onlineContextItemList) > -1;
          if (!studyFoundInOnlineContextlist && !workitem.disableAutoLaunchViewer) {
            let onlineContextItem = new OnlineContextItem(workitem.requestedProcedureId);
            onlineContextItem.token = this.getNewContextToken();
            onlineContextItem = this.setCurrentOnlineContextItem(onlineContextItem, workitem);
            this.contextInfo.onlineContextItemList[this.contextInfo.onlineContextItemList.length] = onlineContextItem;
            this.contextInfo.onlineContextIndex = this.contextInfo.onlineContextItemList.length - 1;
          }
          this.contextInfo.onlineContextIndex = this.findIndexById(
            workitem.requestedProcedureId,
            this.contextInfo.onlineContextItemList
          );
        } else {
          // Reset the online context index for zero image study
          this.contextInfo.onlineContextIndex = -1;
        }
        this.contextInfo.currentIndex = index;
      } else {
        // If the study is not yet opened, add the study in contextItem.
        const item = new ViewerContextItem(workitem.procedureAccessionNo, workitem.requestedProcedureId);
        item.token = this.getNewContextToken();
        item.readOnlyExam = workitem.readOnlyExam;
        item.studyDataAvailability = workitem.studyDataAvailability;
        item.mrn = workitem.mrn;
        item.imageCount = workitem.imageCount;
        item.disableAutoLaunchViewer = workitem.disableAutoLaunchViewer;
        item.archiveAdoptedStatus = workitem.archiveAdoptedStatus;
        item.isGlobalExam = workitem.isGlobalExam;
        item.siteId = workitem.siteId;
        // If study is online add the study in online context list
        if (
          (item.archiveAdoptedStatus === appConstants.archiveAdoptionStatus.ArchiveAdopted ||
            (UtilService.isExamOnline(item.studyDataAvailability) && item.imageCount > 0)) &&
          !item.disableAutoLaunchViewer
        ) {
          let onlineItem = new OnlineContextItem(workitem.requestedProcedureId);
          onlineItem.token = item.token;
          onlineItem = this.setCurrentOnlineContextItem(onlineItem, workitem);
          this.contextInfo.onlineContextItemList[this.contextInfo.onlineContextItemList.length] = onlineItem;
          this.contextInfo.onlineContextIndex = this.contextInfo.onlineContextItemList.length - 1;
        } else {
          // Reset the online context index for zero image study when not archive adopted
          this.contextInfo.onlineContextIndex = -1;
        }

        this.contextInfo.contextItemList[this.contextInfo.contextItemList.length] = item;
        this.contextInfo.currentIndex = this.contextInfo.contextItemList.length - 1;
      }
    }
  }

  setContextById(id) {
    let retValue = false;
    const currentIdex = this.findIndexById(id, this.contextInfo.contextItemList);
    if (currentIdex > -1) {
      this.contextInfo.currentIndex = currentIdex;
      retValue = true;
    }
    const onlineIndex = this.findIndexById(id, this.contextInfo.onlineContextItemList);
    if (onlineIndex > -1) {
      this.contextInfo.onlineContextIndex = onlineIndex;
      retValue = true;
    }
    return retValue;
  }

  findIndexById(id, list: Array<any>) {
    return _.findIndex(list, { NCD: id });
  }

  getNewContextToken() {
    const date = new Date();
    const sec = date.getSeconds();
    const milSec = date.getMilliseconds();

    // Random number generated between 1 and 1000.
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    const randomNumber = (array[0] % (this.contextTokenMax - this.contextTokenMin + 1)) + this.contextTokenMin;
    // Append time in second and millisecond to the random number.
    // Converted to string so that they can concatenate.
    this.token = randomNumber.toString() + sec.toString() + milSec.toString();
    return this.token;
  }

  getCurrentIndexForViewer() {
    return this.contextInfo.onlineContextIndex;
  }

  isRemoteStudy(contextItem: any) {
    return !!contextItem.repositoryType && contextItem.repositoryType !== 'Local';
  }

  getStudyIdentifiersForViewer() {
    let studyIdentifier = '';
    for (const contextItem of this.contextInfo.onlineContextItemList) {
      let identifier = '';
      const comparisons = this.utilService.getComparisons(contextItem.NCD);
      identifier = contextItem.token + '*NCD:';
      identifier += contextItem.NCD + comparisons;

      if ('' === studyIdentifier) {
        studyIdentifier = identifier;
      } else {
        studyIdentifier = studyIdentifier + ';' + identifier;
      }
    }
    return studyIdentifier;
  }

  getStudyIdentifierOfGivenContext(requestedProcedureID: number) {
    const index = this.findIndexById(requestedProcedureID, this.contextInfo.contextItemList);
    if (index > -1) {
      return this.contextInfo.contextItemList[index].accession;
    }
  }

  getCurrentContext() {
    if (-1 === this.contextInfo.currentIndex) {
      return null;
    } else {
      return this.contextInfo.contextItemList[this.contextInfo.currentIndex].NCD;
    }
  }

  getCurrentContextItem(): ViewerContextItem {
    if (-1 === this.contextInfo.currentIndex) {
      return null;
    } else {
      return this.contextInfo.contextItemList[this.contextInfo.currentIndex];
    }
  }

  getContextItem(requestedProcedureID) {
    const index = this.findIndexById(requestedProcedureID, this.contextInfo.contextItemList);
    if (index > -1) {
      return this.contextInfo.contextItemList[index];
    } else {
      return null;
    }
  }

  getContextItemCount() {
    // Return the online study count
    return this.contextInfo.onlineContextItemList.length;
  }

  removeStudyFromContextList(studyIndex) {
    if (studyIndex !== -1) {
      // Rearrange the context items list
      this.contextInfo.contextItemList.splice(studyIndex, 1);
      if (studyIndex < this.contextInfo.currentIndex) {
        // If the removed index is less than the current index then rearrange the current index
        this.contextInfo.currentIndex = this.contextInfo.currentIndex > -1 ? this.contextInfo.currentIndex - 1 : -1;
      } else if (studyIndex === this.contextInfo.currentIndex) {
        // If the removed index and current index are same the reset the current index to -1
        this.contextInfo.currentIndex = -1;
      }
    }
  }

  removeStudyFromOnlineContextList(onlineStudyIndex) {
    if (onlineStudyIndex !== -1) {
      // Rearrange the online context items list
      this.contextInfo.onlineContextItemList.splice(onlineStudyIndex, 1);
      if (onlineStudyIndex < this.contextInfo.onlineContextIndex) {
        // If the removed index is less than the online index then rearrange the online index
        this.contextInfo.onlineContextIndex =
          this.contextInfo.onlineContextIndex > -1 ? this.contextInfo.onlineContextIndex - 1 : -1;
      } else if (onlineStudyIndex === this.contextInfo.onlineContextIndex) {
        // If the removed index and online index are same the reset the online index to -1
        this.contextInfo.onlineContextIndex = -1;
      }
    }
  }

  removeContext(NCD) {
    let onlineStudyIndex = -1;
    // Find the index of given study in context items list
    const studyIndex = this.findIndexById(NCD, this.contextInfo.contextItemList);
    if (studyIndex !== -1) {
      // Check for online studies
      if (
        this.contextInfo.contextItemList[studyIndex].archiveAdoptedStatus ===
          appConstants.archiveAdoptionStatus.ArchiveAdopted ||
        (UtilService.isExamOnline(this.contextInfo.contextItemList[studyIndex].studyDataAvailability) &&
          this.contextInfo.contextItemList[studyIndex].imageCount > 0)
      ) {
        // Find the study index in online context list
        onlineStudyIndex = this.findIndexById(NCD, this.contextInfo.onlineContextItemList);
      }
    }

    // Remove study from context item list
    this.removeStudyFromContextList(studyIndex);

    // Remove study from online context item list
    this.removeStudyFromOnlineContextList(onlineStudyIndex);
  }

  removeAllContext() {
    // Clear the context item list.
    this.contextInfo.contextItemList.splice(0, this.contextInfo.contextItemList.length);
    // Clear the online context item list.
    this.contextInfo.onlineContextItemList.splice(0, this.contextInfo.onlineContextItemList.length);
    // Reset the index
    this.contextInfo.currentIndex = -1;
    // Reset the online context index
    this.contextInfo.onlineContextIndex = -1;
  }

  getContextCorrespondingToAccessionNum(accessionnum) {
    const contextItem = {};
    const accessionNumToSearch = 'accession';
    contextItem[accessionNumToSearch] = accessionnum;
    const studyIdentifier = _.findWhere(this.contextInfo.contextItemList, contextItem);
    return studyIdentifier !== undefined ? studyIdentifier.NCD : '';
  }

  setViewerContext(status) {
    this.contextInfo.viewerContext = status;
  }

  getViewerContext() {
    return this.contextInfo.viewerContext;
  }

  setDiscardFlag(status) {
    this.contextInfo.discardFlag = status;
  }

  getCloseFlag() {
    return this.contextInfo.closeFlag;
  }

  setCloseFlag(status) {
    this.contextInfo.closeFlag = status;
  }

  getDiscardFlag() {
    return this.contextInfo.discardFlag;
  }

  checkStudyInContext(id) {
    const studyIdentifier = this.getContextListItem(id);
    return studyIdentifier !== undefined ? true : false;
  }

  isViewerOpened(id) {
    const studyIdentifier = this.getContextListItem(id);
    return _.isUndefined(studyIdentifier) ? false : studyIdentifier.isViewerOpened;
  }

  getContextListItem(id) {
    const contextItem = {};
    const ncdToSearch = 'NCD';
    contextItem[ncdToSearch] = id;
    return _.findWhere(this.contextInfo.contextItemList, contextItem);
  }

  isReadOnlyExam(studyId) {
    const contextItem = {};
    const ncdToSearch = 'NCD';
    contextItem[ncdToSearch] = studyId;
    const studyIdentifier = _.findWhere(this.contextInfo.contextItemList, contextItem);
    return studyIdentifier !== undefined ? studyIdentifier.readOnlyExam : false;
  }

  isStudyDataAvailable(studyId) {
    const studyIdentifier = _.findWhere(this.contextInfo.contextItemList, {
      NCD: studyId
    });
    const studyAvailability = studyIdentifier !== undefined ? studyIdentifier.studyDataAvailability : 'UNDEFINED';
    const imageCount = studyIdentifier !== undefined ? studyIdentifier.imageCount : 0;
    const archiveAdoptedStatus =
      studyIdentifier !== undefined ? studyIdentifier.archiveAdoptedStatus : appConstants.archiveAdoptionStatus.NO;
    const isStudyDataAvailable =
      archiveAdoptedStatus === appConstants.archiveAdoptionStatus.ArchiveAdopted ||
      (UtilService.isExamOnline(studyAvailability) && imageCount > 0);
    const isStudyExistsInOnlineContextlist = this.findIndexById(studyId, this.contextInfo.onlineContextItemList) > -1;
    return isStudyDataAvailable && isStudyExistsInOnlineContextlist;
  }

  setStudyClickTime(studyClickTime) {
    this.contextInfo.studyClickTime = studyClickTime;
  }

  getStudyClickTime() {
    return this.contextInfo.studyClickTime;
  }

  getPriorContext(primaryId) {
    return this.priorContextMap.get(primaryId);
  }

  setPriorContextMap(primaryId, context) {
    // Remove the primary context if exist from context list
    // to avoid multiple context items against single tab
    this.removeContext(primaryId);
    this.priorContextMap.set(primaryId, context);
  }

  removeFromPriorContextMap(key) {
    this.priorContextMap.delete(key);
  }

  getDocScanStatus(studyId) {
    return this.docScanStatusMap.get(studyId);
  }

  setDocScanStatus(studyId, status) {
    this.docScanStatusMap.set(studyId, status);
  }

  removeDocScanStatus(studyId) {
    this.docScanStatusMap.delete(studyId);
  }
}
